import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";

import { saveConditionsIfDirtyAtom } from "../stores/conditions";
import {
  clientEditorAtom,
  loadClientEditorAtom,
  saveClientAtom,
} from "../stores/editor";

export type UseClientEditorOptions = {
  clientId?: number | null;
  onSaved?: (clientId: number) => void;
  onCancel?: () => void;
};

export type UseClientEditor = {
  loading: boolean;
  saving: boolean;
  error: string | null;
  conflict: boolean;
  save: () => Promise<void>;
  cancel: () => void;
};

export function useClientEditor({
  clientId = null,
  onSaved,
  onCancel,
}: UseClientEditorOptions): UseClientEditor {
  const { loading, saving, error, conflict } = useAtomValue(clientEditorAtom);

  const load = useSetAtom(loadClientEditorAtom);
  const saveClient = useSetAtom(saveClientAtom);
  const saveConditions = useSetAtom(saveConditionsIfDirtyAtom);

  useEffect(() => {
    void load(clientId);
  }, [load, clientId]);

  const save = useCallback(async () => {
    const savedId = await saveClient();

    if (null === savedId) {
      return;
    }

    // The terms are a record of their own, saved from the same button: their
    // own lock version is the client's, so they go second.
    if (!(await saveConditions(savedId))) {
      return;
    }

    onSaved?.(savedId);
  }, [saveClient, saveConditions, onSaved]);

  const cancel = useCallback(() => onCancel?.(), [onCancel]);

  return { loading, saving, error, conflict, save, cancel };
}
